import { useEffect, useRef } from "react";
import { order_poll_fallback_enabled } from "../Pages/apis";
import { subscribeToOrders } from "./echo";

/**
 * Listens for OrderChanged websocket events and calls `onRefresh` so the
 * dashboard reloads orders immediately. Optionally keeps a slow fallback
 * poll (controlled by REACT_APP_ORDER_POLL_FALLBACK) if websockets drop.
 *
 * @param {() => void} onRefresh  existing getOrders / getAllOrders callback
 * @param {object} [options]
 * @param {number|null} [options.departmentId]  only refresh for matching dept (null = always)
 * @param {number|null} [options.roomId]  only refresh for matching room (null = any room)
 * @param {number} [options.fallbackMs]  slow poll interval while websockets are primary
 * @param {boolean} [options.enableFallback]  override REACT_APP_ORDER_POLL_FALLBACK
 * @param {(payload: object) => void} [options.onEvent]  raw event callback (e.g. for toasts)
 */
export const useOrderRealtime = (onRefresh, options = {}) => {
  const {
    departmentId = null,
    roomId = null,
    fallbackMs = 15000,
    enableFallback = order_poll_fallback_enabled,
    onEvent = null,
  } = options;
  const onRefreshRef = useRef(onRefresh);
  const onEventRef = useRef(onEvent);
  const roomIdRef = useRef(roomId);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    const handleEvent = (payload) => {
      onEventRef.current?.(payload);

      if (
        departmentId != null &&
        payload?.department_id != null &&
        Number(payload.department_id) !== Number(departmentId) &&
        payload?.action !== "checked_out"
      ) {
        return;
      }

      const openRoomId = roomIdRef.current;
      if (
        openRoomId != null &&
        payload?.room_id != null &&
        Number(payload.room_id) !== Number(openRoomId) &&
        payload?.action !== "checked_out"
      ) {
        // Still refresh open room panel on checkout of that room only;
        // skip unrelated rooms.
        return;
      }

      onRefreshRef.current?.(payload);
    };

    const unsubscribe = subscribeToOrders(handleEvent);

    let interval = null;
    if (enableFallback && Number(fallbackMs) > 0) {
      interval = setInterval(() => {
        onRefreshRef.current?.();
      }, fallbackMs);
    }

    return () => {
      unsubscribe();
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [departmentId, fallbackMs, enableFallback]);
};
